import { LINK_TYPE } from './link-type';

const getLinkType = (entity) => entity.entityContent?.linkType ?? 'link';

export const isLinkType = (selectedLinkType) => (entity) =>
  getLinkType(entity) === selectedLinkType;

export const byRelevance = (a, b) => {
  const { entityContent: entityContentA } = a;
  const relevanceA = entityContentA.relevance ?? Infinity;
  const personalisationRelevanceA =
    entityContentA.personalisationRelevance ?? 0;

  const { entityContent: entityContentB } = b;
  const relevanceB = entityContentB.relevance ?? Infinity;
  const personalisationRelevanceB =
    entityContentB.personalisationRelevance ?? 0;

  const relevanceDiff = personalisationRelevanceB - personalisationRelevanceA;

  // Higher personalisation relevance takes priority
  if (relevanceDiff !== 0) return relevanceDiff;

  // Fall back to ascending relevance comparison
  return relevanceA - relevanceB;
};

export const byAlphabetical = (a, b) => {
  const nameA = a.entityContent.name;
  const nameB = b.entityContent.name;
  return nameA.localeCompare(nameB);
};

export const byReverseAlphabetical = (a, b) => {
  const nameA = a.entityContent.name;
  const nameB = b.entityContent.name;
  return nameB.localeCompare(nameA);
};

export const pairLinks = (items) => {
  if (!Array.isArray(items) || items.length === 0) {
    return [];
  }

  // Store original index of each link
  const linkPositions = items.reduce((acc, item, i) => {
    if (isLinkType(LINK_TYPE.LINK)(item)) {
      return [...acc, i];
    }
    return acc;
  }, []);

  // Store link pairs
  const links = items.filter(isLinkType(LINK_TYPE.LINK));

  const pairs = links.reduce((acc, _, i) => {
    const isIndexEven = i % 2 === 0;
    const isLast = i + 1 === links.length;
    if (isIndexEven) {
      acc.push(isLast ? [links[i]] : [links[i], links[i + 1]]);
    }
    return acc;
  }, []);

  // No pairs found, return early
  if (!pairs.length) {
    return items.map((item) => ({ renderType: 'single', item }));
  }

  const singles = items.filter((item) => !isLinkType(LINK_TYPE.LINK)(item));

  // Build result
  const result = [];
  let nextLinkPosition = linkPositions.shift();
  let nextPair = pairs.shift();
  let nextSingle = singles.shift();

  for (let i = 0; i < items.length; i++) {
    if (i === nextLinkPosition && nextPair !== undefined) {
      result.push({ renderType: 'pair', items: nextPair });
      nextLinkPosition = linkPositions.shift();
      nextPair = pairs.shift();
    } else if (nextSingle !== undefined) {
      result.push({ renderType: 'single', item: nextSingle });
      nextSingle = singles.shift();
    }
  }

  return result;
};

export const buildPersonalisationTally = (personalisationResponses) => {
  const personalisationTally = Object.values(personalisationResponses)
    .flat()
    .reduce((acc, answerId) => {
      acc[answerId] = (acc[answerId] || 0) + 1;
      return acc;
    }, {});
  return personalisationTally;
};

export const enrichWithPersonalisationRelevance =
  (personalisationTally) => (entity) => {
    const { entityContent } = entity;
    const { personalisationTags } = entityContent ?? {};
    const personalisationRelevance =
      personalisationTags?.reduce((acc, tag) => {
        const increment = personalisationTally[tag] ?? 0;
        return acc + increment;
      }, 0) ?? 0;

    return {
      ...entity,
      entityContent: {
        ...entityContent,
        personalisationRelevance,
      },
    };
  };

// Utils copied over from Links/Admin. Could be extracted to frontend common as reusable utils

/**
 * filterLinksByCategory
 * @param {array} categories - list of selected categories to be matched
 * @param {array} links - list of links data
 * @returns {array} filtered list of links data based on categories
 */
export const filterLinksByCategory = (selectedCategoryIds, links) => {
  if (!selectedCategoryIds.length) {
    return links;
  }

  return links?.filter((link) => {
    const linkCategoryIds = link?.entityContent?.categories.map(({ id }) => id);
    return linkCategoryIds.some((linkCategoryId) =>
      selectedCategoryIds.includes(linkCategoryId)
    );
  });
};
